import React, { useMemo } from 'react';
import { TrendingUp } from 'lucide-react';

const ConfidenceHealthCorrelation = ({ data }) => {
  const correlationData = useMemo(() => {
    const points = data
      .filter(item => item.confidenceScore > 0 && item.healthScore > 0)
      .map(item => ({
        changeNumber: item.changeNumber,
        confidence: item.confidenceScore,
        health: item.healthScore,
        entityType: item.entityType,
        status: item.health
      }));

    // Calculate correlation coefficient
    const n = points.length;
    if (n === 0) return { points: [], correlation: 0, trend: null };

    const sumX = points.reduce((sum, p) => sum + p.confidence, 0);
    const sumY = points.reduce((sum, p) => sum + p.health, 0);
    const sumXY = points.reduce((sum, p) => sum + p.confidence * p.health, 0);
    const sumX2 = points.reduce((sum, p) => sum + p.confidence * p.confidence, 0);
    const sumY2 = points.reduce((sum, p) => sum + p.health * p.health, 0);

    const correlation = (n * sumXY - sumX * sumY) / 
      Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    // Calculate trend line
    const meanX = sumX / n;
    const meanY = sumY / n;
    const slope = (sumXY - n * meanX * meanY) / (sumX2 - n * meanX * meanX);
    const intercept = meanY - slope * meanX;

    return {
      points,
      correlation: isNaN(correlation) ? 0 : correlation,
      trend: { slope, intercept }
    };
  }, [data]);

  const getHealthColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#eab308';
    if (score >= 30) return '#f97316';
    return '#ef4444';
  };

  const getCorrelationStrength = (r) => {
    const abs = Math.abs(r);
    if (abs >= 0.7) return 'Strong';
    if (abs >= 0.4) return 'Moderate';
    if (abs >= 0.2) return 'Weak';
    return 'Very Weak';
  };

  const getCorrelationColor = (r) => {
    const abs = Math.abs(r);
    if (abs >= 0.7) return '#10b981';
    if (abs >= 0.4) return '#eab308';
    return '#ef4444';
  };

  return (
    <div className="bg-dark-card rounded-lg border border-dark-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp size={24} className="text-cyan-accent" />
        <h3 className="text-xl font-bold text-cyan-accent">Confidence vs Health Correlation</h3>
      </div>

      {correlationData.points.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <TrendingUp size={48} className="mx-auto mb-4 opacity-50" />
          <p>No correlation data available</p>
          <p className="text-sm mt-2">Need services with both confidence and health scores</p>
        </div>
      ) : (
        <>
          {/* Correlation Stats */}
          <div className="mb-6 grid grid-cols-3 gap-4">
            <div className="bg-dark-border/20 rounded-lg p-4 border border-dark-border/60 text-center">
              <div 
                className="text-4xl font-bold mb-2"
                style={{ color: getCorrelationColor(correlationData.correlation) }}
              >
                {correlationData.correlation.toFixed(3)}
              </div>
              <div className="text-sm text-gray-400">Correlation (r)</div>
              <div 
                className="text-xs mt-1"
                style={{ color: getCorrelationColor(correlationData.correlation) }}
              >
                {getCorrelationStrength(correlationData.correlation)}
              </div>
            </div>
            <div className="bg-dark-border/20 rounded-lg p-4 border border-dark-border/60 text-center">
              <div className="text-4xl font-bold text-cyan-accent mb-2">
                {correlationData.points.length}
              </div>
              <div className="text-sm text-gray-400">Data Points</div>
            </div>
            <div className="bg-dark-border/20 rounded-lg p-4 border border-dark-border/60 text-center">
              <div className="text-4xl font-bold text-purple-accent mb-2">
                {(correlationData.correlation * correlationData.correlation * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-400">R² (Variance)</div>
            </div>
          </div>

          {/* Scatter Plot */}
          <div className="bg-black/40 rounded-lg border border-dark-border/60 p-6 relative" style={{ height: '500px' }}>
            {/* Grid */}
            <div className="absolute inset-6 grid grid-cols-10 grid-rows-10 gap-0">
              {Array(100).fill(0).map((_, i) => (
                <div key={i} className="border border-dark-border/20"></div>
              ))}
            </div>

            {/* Axes */}
            <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-500">
              Confidence Score →
            </div>
            <div 
              className="absolute top-0 bottom-0 left-2 flex items-center text-xs text-gray-500"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              Health Score →
            </div>

            {/* Axis labels */}
            <div className="absolute bottom-6 left-6 right-6 flex justify-between text-xs text-gray-600">
              <span>0</span>
              <span>25</span>
              <span>50</span>
              <span>75</span>
              <span>100</span>
            </div>
            <div className="absolute top-6 bottom-6 left-6 flex flex-col justify-between text-xs text-gray-600">
              <span>100</span>
              <span>75</span>
              <span>50</span>
              <span>25</span>
              <span>0</span>
            </div>

            {/* Trend line */}
            {correlationData.trend && (
              <svg className="absolute inset-6 w-[calc(100%-48px)] h-[calc(100%-48px)]" style={{ overflow: 'visible' }}>
                <line
                  x1="0"
                  y1={`${100 - (correlationData.trend.intercept)}%`}
                  x2="100%"
                  y2={`${100 - (correlationData.trend.slope * 100 + correlationData.trend.intercept)}%`}
                  stroke="#00e5ff"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  opacity="0.6"
                />
              </svg>
            )}

            {/* Data points */}
            <div className="absolute inset-6">
              {correlationData.points.map((point, idx) => {
                const x = (point.confidence / 100) * 100;
                const y = 100 - (point.health / 100) * 100;
                
                return (
                  <div
                    key={idx}
                    className="absolute rounded-full cursor-pointer transition-all hover:scale-150"
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      width: '8px',
                      height: '8px',
                      backgroundColor: getHealthColor(point.health),
                      boxShadow: `0 0 10px ${getHealthColor(point.health)}80`,
                      transform: 'translate(-50%, -50%)'
                    }}
                    title={`${point.changeNumber}\nConfidence: ${point.confidence}%\nHealth: ${point.health}`}
                  />
                );
              })}
            </div>
          </div>

          {/* Interpretation */}
          <div className="mt-6 bg-dark-border/20 rounded-lg p-4 border border-dark-border/60">
            <div className="text-sm font-semibold text-white mb-2">Interpretation:</div>
            <div className="text-sm text-gray-400">
              {correlationData.correlation > 0.7 ? (
                <>
                  <span className="text-lime-accent font-semibold">Strong positive correlation</span> - 
                  Higher confidence scores are strongly associated with better health. 
                  The matching algorithm is performing well.
                </>
              ) : correlationData.correlation > 0.4 ? (
                <>
                  <span className="text-yellow-accent font-semibold">Moderate positive correlation</span> - 
                  There's a reasonable relationship between confidence and health. 
                  Some improvements to matching may help.
                </>
              ) : correlationData.correlation > 0 ? (
                <>
                  <span className="text-orange-accent font-semibold">Weak positive correlation</span> - 
                  Confidence scores don't strongly predict health. 
                  Consider reviewing the matching algorithm.
                </>
              ) : correlationData.correlation > -0.4 ? (
                <>
                  <span className="text-red-500 font-semibold">Weak negative correlation</span> - 
                  Higher confidence may be associated with worse health. 
                  This suggests potential issues with the matching process.
                </>
              ) : (
                <>
                  <span className="text-red-500 font-semibold">Strong negative correlation</span> - 
                  Higher confidence is associated with worse health. 
                  The matching algorithm needs immediate review.
                </>
              )}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 flex items-center justify-center gap-6 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-lime-accent"></div>
              <span className="text-gray-400">Healthy (80-100)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-accent"></div>
              <span className="text-gray-400">Degraded (60-79)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-accent"></div>
              <span className="text-gray-400">Warning (30-59)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-gray-400">Critical (0-29)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-0.5 bg-cyan-accent" style={{ borderStyle: 'dashed' }}></div>
              <span className="text-gray-400">Trend Line</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ConfidenceHealthCorrelation;
